import { Avatar } from 'vnetwork-ui'
import { ChatMessage } from '../../types/response'
import { formatFromUTC } from '@/utils/function'
import { Bot, User } from 'lucide-react'

interface MessageItemProps {
    message: ChatMessage
    isOwnMessage?: boolean
}

// Get display name for message sender
const getSenderName = (message: ChatMessage): string => {
    if (message.user?.name) return message.user.name
    switch (message.sender) {
        case 'agent':
            return 'Bot'
        case 'system':
            return 'System'
        case 'employee':
            return 'Support'
        default:
            return message.sender
    }
}

// System message - centered, simple text
const SystemMessage: React.FC<{ message: ChatMessage }> = ({ message }) => (
    <div className="flex justify-center my-3">
        <div className="flex items-center gap-2 text-xs text-gray-500">
            <span>{message.content}</span>
            <span className="text-gray-400">
                {formatFromUTC(message.createdAt, 'HH:mm')}
            </span>
        </div>
    </div>
)

// Agent (Bot) message - left side with bot avatar
const AgentMessage: React.FC<{ message: ChatMessage }> = ({ message }) => (
    <div className="flex justify-start gap-2.5 mb-3 w-full">
        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center flex-shrink-0 shadow-sm">
            <Bot size={18} className="text-white" />
        </div>
        <div className="max-w-[70%]">
            <div className="mb-1">
                <span className="text-xs font-semibold text-blue-600">Bot</span>
            </div>
            <div className="bg-white rounded-2xl rounded-tl-md px-4 py-2.5 shadow-sm">
                {renderContent(message)}
            </div>
            <div className="mt-1 ml-1">
                <span className="text-[10px] text-gray-400">
                    {formatFromUTC(message.createdAt, 'HH:mm')}
                </span>
            </div>
        </div>
    </div>
)

// User (Customer) message - left side with user avatar
const UserMessage: React.FC<{ message: ChatMessage }> = ({ message }) => (
    <div className="flex justify-start gap-2.5 mb-3 w-full">
        {message.user?.avatar ? (
            <Avatar
                src={message.user.avatar}
                alt={message.user.name}
                className="w-9 h-9 flex-shrink-0 shadow-sm"
            />
        ) : (
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-gray-400 to-gray-500 flex items-center justify-center flex-shrink-0 shadow-sm">
                <User size={18} className="text-white" />
            </div>
        )}
        <div className="max-w-[70%]">
            <div className="mb-1">
                <span className="text-xs font-semibold text-gray-700">
                    {getSenderName(message)}
                </span>
            </div>
            <div className="bg-white rounded-2xl rounded-tl-md px-4 py-2.5 shadow-sm">
                {renderContent(message)}
            </div>
            <div className="mt-1 ml-1">
                <span className="text-[10px] text-gray-400">
                    {formatFromUTC(message.createdAt, 'HH:mm')}
                </span>
            </div>
        </div>
    </div>
)

// Employee message (own) - right side, primary color
const EmployeeOwnMessage: React.FC<{ message: ChatMessage }> = ({
    message,
}) => (
    <div className="flex justify-end mb-3 w-full">
        <div className="max-w-[70%]">
            <div className="flex justify-end mb-1">
                <span className="text-xs font-semibold text-blue-600">You</span>
            </div>
            <div className="bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-2xl rounded-tr-md px-4 py-2.5 shadow-sm">
                {renderContent(message, true)}
            </div>
            <div className="mt-1 mr-1 flex justify-end">
                <span className="text-[10px] text-gray-400">
                    {formatFromUTC(message.createdAt, 'HH:mm')}
                </span>
            </div>
        </div>
    </div>
)

// Employee message (other employee) - left side with avatar
const EmployeeOtherMessage: React.FC<{ message: ChatMessage }> = ({
    message,
}) => (
    <div className="flex justify-start gap-2.5 mb-3 w-full">
        {message.user?.avatar ? (
            <Avatar
                src={message.user.avatar}
                alt={message.user.name}
                className="w-9 h-9 flex-shrink-0 shadow-sm"
            />
        ) : (
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center flex-shrink-0 shadow-sm">
                <User size={18} className="text-white" />
            </div>
        )}
        <div className="max-w-[70%]">
            <div className="mb-1">
                <span className="text-xs font-semibold text-emerald-600">
                    {getSenderName(message)}
                </span>
            </div>
            <div className="bg-emerald-50 border border-emerald-100 rounded-2xl rounded-tl-md px-4 py-2.5 shadow-sm">
                {renderContent(message)}
            </div>
            <div className="mt-1 ml-1">
                <span className="text-[10px] text-gray-400">
                    {formatFromUTC(message.createdAt, 'HH:mm')}
                </span>
            </div>
        </div>
    </div>
)

// Render message content based on type
const renderContent = (message: ChatMessage, isWhiteText = false) => {
    const textClass = isWhiteText ? 'text-white' : 'text-gray-800'

    switch (message.type) {
        case 'image':
            return (
                <div>
                    {message.fileUrl && (
                        <img
                            src={message.fileUrl}
                            alt={message.fileName || 'Image'}
                            className="rounded-lg max-h-48 cursor-pointer"
                            onClick={() =>
                                window.open(message.fileUrl, '_blank')
                            }
                        />
                    )}
                    {message.content && (
                        <p className={`mt-1 text-sm ${textClass}`}>
                            {message.content}
                        </p>
                    )}
                </div>
            )
        case 'audio':
            return (
                <audio controls src={message.fileUrl} className="max-w-full" />
            )
        case 'video':
            return (
                <video
                    controls
                    src={message.fileUrl}
                    className="rounded-lg max-h-48"
                />
            )
        case 'file':
            return (
                <a
                    href={message.fileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`flex items-center gap-2 ${isWhiteText ? 'text-blue-100 hover:text-white' : 'text-blue-600 hover:underline'}`}
                >
                    <span>📎 {message.fileName || 'Download file'}</span>
                    {message.fileSize && (
                        <span className="text-xs opacity-70">
                            ({(message.fileSize / 1024).toFixed(1)} KB)
                        </span>
                    )}
                </a>
            )
        case 'sticker':
            return message.fileUrl ? (
                <img
                    src={message.fileUrl}
                    alt="Sticker"
                    className="w-24 h-24 object-contain"
                />
            ) : (
                <span className="text-3xl">{message.content}</span>
            )
        default:
            return (
                <p className={`text-sm whitespace-pre-wrap ${textClass}`}>
                    {message.content}
                </p>
            )
    }
}

const MessageItem: React.FC<MessageItemProps> = ({ message, isOwnMessage }) => {
    // System message
    if (message.sender === 'system') {
        return <SystemMessage message={message} />
    }

    // Agent (Bot) message
    if (message.sender === 'agent') {
        return <AgentMessage message={message} />
    }

    // User (Customer) message
    if (message.sender === 'user') {
        return <UserMessage message={message} />
    }

    // Employee message
    if (message.sender === 'employee') {
        if (isOwnMessage) {
            return <EmployeeOwnMessage message={message} />
        }
        return <EmployeeOtherMessage message={message} />
    }

    // Fallback
    return <UserMessage message={message} />
}

export default MessageItem
